import { PageHeader, Text } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { EpisodeCategory } from '@common/enums';
import type { IServiceProvider } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { serviceCodeButtonOrder } from '@common/customControls/ggzConsultServiceCodeSortOrder';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { Button } from '../../../elements/common';
import { SettingsCategoryFilterButtonRow } from '../../../elements/SettingsCategoryFilterButtonRow';
import { usePersonalSettings } from '../PersonalSettingsContext';
import { fetchPreferenceListServiceProviders } from './personalSettingsPreferenceListHelper';
import { PersonalSettingsPreferenceListExaminationSelectConsumers } from './PreferenceListExamination/PersonalSettingsPreferenceListExaminationSelectConsumers';

export function PersonalSettingsPreferenceListProviderContainer() {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const { employmentId, episodeCategory } = useParams() as { employmentId: string; episodeCategory: EpisodeCategory };

    const { refetch: loadPersonalSettingsData, isReadOnly } = usePersonalSettings();

    const [serviceProviders, setServiceProviders] = useState<IServiceProvider[]>([]);
    const [selectedServiceProvider, setSelectedServiceProvider] = useState<IServiceProvider>();
    assertHasAuthorization(ManagerAuthorization.employmentSettingsRead);

    useEffect(() => {
        loadServiceProviders();
    }, [employmentId, episodeCategory]);

    if (serviceProviders.length === 0) {
        return null;
    }

    return (
        <>
            <PageHeader title={getTitle()} />
            {serviceProviders.length > 1 && (
                <SettingsCategoryFilterButtonRow
                    collection={serviceProviders.map((provider) => ({
                        displayText: provider.service.name,
                        ...provider,
                    }))}
                    onClick={setSelectedServiceProvider}
                    selectedItem={selectedServiceProvider}
                />
            )}
            {selectedServiceProvider?.service.serviceType === ServiceType.examination && (
                <PersonalSettingsPreferenceListExaminationSelectConsumers
                    isReadOnly={isReadOnly}
                    key={selectedServiceProvider.id}
                    onDeleteOfLastConsumer={() => loadPersonalSettingsData}
                    serviceProviderId={selectedServiceProvider.id}
                />
            )}
            {(selectedServiceProvider?.service.serviceType === ServiceType.referral ||
                selectedServiceProvider?.service.serviceType === ServiceType.consultation) &&
                renderPageNotFoundText()}
        </>
    );

    function renderPageNotFoundText() {
        return (
            <>
                <Text mb={8}>{translate.errorResponses.notFound}</Text>
                <Button
                    color="secondary"
                    large
                    onClick={() => navigate(-1)}
                    outlined
                >
                    {translate.common.goBack}
                </Button>
            </>
        );
    }

    async function loadServiceProviders() {
        const providers = await fetchPreferenceListServiceProviders(employmentId, episodeCategory);

        const sortedProviders = sortBy(
            providers,
            (provider) => serviceCodeButtonOrder.indexOf(provider.service.code!),
            (provider) => provider.service.name
        );

        if (sortedProviders.length > 0) {
            setSelectedServiceProvider(sortedProviders[0]);
        }

        setServiceProviders(sortedProviders);
    }

    function getTitle() {
        const applicants = translate.episodeCategory.applicants;
        const preferenceList = translate.personalSettings.preferenceList.preferenceList;
        const episodeCategoryName = translate.episodeCategoryEnum[episodeCategory];

        let title = `${preferenceList} ${episodeCategoryName}`;

        if (isManager) {
            title += ` ${applicants}`;
        }

        return title;
    }
}
